import React, { useState } from "react"
import { AppFunctionComponent } from "../../../types"
import styled, { css } from "styled-components"
import { TooltipBox, TooltipText } from "./tooltip.component"

const VerticalTooltip = styled(TooltipBox)`
  text-align: center;
  visibility: hidden;
  opacity: 0;
  transition: visibility 0s, opacity 0.5s ease;
`

const VerticalTooltipContainer = styled.div`
  position: absolute;
  display: none;
  align-items: center;
  left: calc(var(--time-width) + 50px);
  top: 50%;
  transform: translateY(-50%);
  width: 350px;
  transition: top 5s, position 5s;
`

const Time = styled.div`
  --time-circle-radius: 4px;

  box-sizing: border-box;
  position: relative;
  display: flex;
  justify-content: center;
  width: var(--time-width);
  min-height: 70px;
  padding: 20px 0;
  align-items: flex-start;
  font-size: ${18 / 16}rem;
  line-height: ${20 / 18}em;
  text-align: center;
  font-family: ${({ theme }) => theme.font.display};
  border-top: 2px solid ${({ theme }) => theme.color.chartCircleFill};
  transition: font-size 0.5s, font-weight 0.5s;

  &:before {
    content: "";
    position: absolute;
    display: block;
    left: calc(50% - var(--time-circle-radius));
    top: calc((var(--time-circle-radius) + 1px) * -1);
    border: var(--time-circle-radius) solid
      ${({ theme }) => theme.color.chartCircleFill};
    border-radius: 50%;
    transition: border-color 0.5s;
  }

  &:hover {
    cursor: pointer;

    &:before {
      border-color: ${({ theme }) => theme.color.chartCircleHover};
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    justify-content: flex-end;
    align-items: center;
    width: var(--time-width);
    height: 100%;
    padding-right: 50px;
    font-size: ${23 / 16}rem;
    white-space: pre-line;
    word-spacing: 15px;
    text-align: right;
    border-top: none;
    border-right: 2px solid ${({ theme }) => theme.color.chartCircleFill};

    &:before {
      left: auto;
      top: calc(50% - var(--time-circle-radius));
      right: calc((var(--time-circle-radius) + 1px) * -1);
    }

    &:hover {
      font-size: ${34 / 16}rem;
    }
  }
`

const Event = styled.div<{ active: boolean }>`
  display: flex;
  flex: 1;
  justify-content: center;

   ${({ active }) =>
     active &&
     css`
       ${Time} {
         font-weight: 500;
         cursor: auto;

         &:before {
           border-color: ${({ theme }) => theme.color.chartCircleActive};
         }
       }
     `}

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    justify-content: flex-start;

    ${VerticalTooltipContainer} {
      display: flex;
    }

    ${({ active }) =>
      active &&
      css`
      position: relative;

      ${Time} {
        font-weight: 400;
        font-size: ${47 / 16}rem;

        &:before {
          border-color: ${({ theme }) => theme.color.chartCircleActive};
        }
      }

      ${VerticalTooltip} {
        visibility: visible;
        opacity: 1;
      }
    }
    `}
  `

const VerticalContainer = styled.div`
  --time-width: 100%;

  display: flex;

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    --time-width: 175px;
    flex-direction: column;
    flex-wrap: wrap;
    height: 100%;
  }
`

const HorizontalTooltip = styled(TooltipBox)`
  margin: 10px 30px 30px 30px;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    display: none;
  }
`

const HorizontalContainer = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    height: 100%;
  }
`

interface Props {
  events: {
    time: string
    text: string
  }[]
}

const Timeline: AppFunctionComponent<Props> = ({ events }) => {
  const [activeEventIndex, setActiveEventIndex] = useState(0)

  const setActiveEventIndexHandle = (index: number) => () => {
    setActiveEventIndex(index)
  }

  return (
    <HorizontalContainer>
      <VerticalContainer>
        {events.map(({ time, text }, index) => (
          <Event key={index} active={activeEventIndex === index}>
            <Time onClick={setActiveEventIndexHandle(index)}>{time}</Time>
            <VerticalTooltipContainer>
              <VerticalTooltip>
                <TooltipText>{text}</TooltipText>
              </VerticalTooltip>
            </VerticalTooltipContainer>
          </Event>
        ))}
      </VerticalContainer>
      <HorizontalTooltip>
        <TooltipText>{events[activeEventIndex].text}</TooltipText>
      </HorizontalTooltip>
    </HorizontalContainer>
  )
}

export default Timeline
